/**
 * WeaveIn.ai Platform - User Management API Service
 * Handles user invitations, password management, and user administration
 */

import axios, { AxiosInstance } from 'axios';
import { config as productConfig } from '@/src/config';
import type {
  AssignableRole,
  UserProgramAssignment,
  UserProgramAssignmentCreate,
  ProgramRoleChange,
  UserEffectivePermissions,
  ProgramLead,
} from '@/types/roleTerminology';

// ============================================================================
// MEMBER ASSIGNMENT TYPES
// ============================================================================

export interface MemberAssignment {
  id: number;
  member_id: number;
  member_name: string;
  access_level: 'full' | 'view_only';
  assigned_at: string;
}

export interface MemberAssignmentCreate {
  member_id: number;
  access_level?: 'full' | 'view_only';
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface UserInvitationCreate {
  email: string;
  name: string;
  role: 'super_admin' | 'org_admin' | 'ops_admin' | 'program_manager' | 'supervisor' | 'care_coordinator' | 'promotora' | 'manager' | 'staff' | 'viewer' | 'member';
  additional_roles?: string[];
  phone_number?: string;
  program_assignments?: unknown[];
  send_now?: boolean;
  // Per-invite 2FA policy. Omit for default (2FA required once the user has a
  // phone). false = exempt this user from 2FA (overrides org enforcement);
  // true = explicitly required.
  require_2fa?: boolean;
}

export interface UserInvitation {
  id: number;
  email: string;
  name: string;
  cbo_id: number;
  role: string;
  additional_roles?: string[];
  invited_by: number;
  // 'pending_approval' (TRUS-513) is a super_admin grant parked for a second
  // super admin: the row exists and carries a token, but no email was sent and
  // the token is inert until approval flips it to 'pending'.
  status: 'pending' | 'held' | 'pending_approval' | 'accepted' | 'expired' | 'cancelled';
  expires_at: string;
  created_at: string;
  organization_name?: string;
  invited_by_name?: string;
  phone_number?: string;  // pre-assigned 2FA number (staff invites)
  require_2fa?: boolean;  // per-invite 2FA policy: undefined/null=default, false=exempt, true=required
  invitation_type?: 'user' | 'member';  // 'user' for staff/admin, 'member' for member portal
  // TRUS-186: org-role display name resolved from role_terminology
  // (role_type -> display_name_en), e.g. "case_manager" -> "Case Manager".
  // Null when the stored role isn't a role_terminology entry for this org.
  // The Staff Invitations table prefers this over the raw role_type.
  role_names?: string | null;
  // BUG-006: backend sets this when status='accepted' AND the matching
  // users row has last_login IS NULL — i.e. the invitee finished
  // registration but never actually signed in. Surfaced in the Staff
  // Invitations list as a sub-status chip so admins can chase the user.
  awaiting_first_login?: boolean;
}

export interface BulkInviteRequest {
  /** Omit for every unclaimed staff account in the org. */
  user_ids?: number[];
  /** false creates the rows but holds them; release later from Invitations. */
  send_now?: boolean;
  /** Server default is true. Pass false only after showing the dry run. */
  dry_run?: boolean;
  /** 1-30, server default 7. */
  expires_in_days?: number;
}

export interface BulkInviteCandidate {
  user_id: number;
  email: string | null;
  name: string | null;
  role: string | null;
  invitation_id?: number;
  /** False when this person holds a program-scoped role that reaches no
   *  program — they can sign in, but the member list will be empty. */
  has_member_visibility?: boolean;
}

export interface BulkInviteSkip {
  user_id: number;
  email: string | null;
  name?: string | null;
  role?: string | null;
  /** portal_member | platform_admin_role | no_email | inactive |
   *  already_claimed | invitation_already_pending | not_in_organization */
  reason: string;
}

export interface BulkInviteWarning {
  code: string;
  count: number;
  message: string;
  users: BulkInviteCandidate[];
}

export interface BulkInviteResult {
  dry_run: boolean;
  organization_name: string;
  /** Dry run only. */
  would_invite?: number;
  /** Real run only. */
  created?: number;
  sending?: number;
  failed?: number;
  skipped: number;
  invitations: BulkInviteCandidate[];
  skipped_detail: BulkInviteSkip[];
  failed_detail?: Array<BulkInviteCandidate & { error: string }>;
  warnings: BulkInviteWarning[];
  without_member_visibility: number;
  message: string;
}

export interface UserRegistrationWithInvite {
  invitation_token: string;
  password: string;
  phone_number?: string;
  terms_accepted: boolean;
  hipaa_accepted: boolean;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordReset {
  reset_token: string;
  new_password: string;
}

export interface PasswordChange {
  current_password: string;
  new_password: string;
}

export interface SelfServiceSignup {
  name: string;
  email: string;
  organization_name?: string;
  phone_number: string;
  password: string;
  terms_accepted: boolean;
  hipaa_accepted: boolean;
  request_sandbox?: boolean;
}

export interface SelfServiceSignupResponse {
  message: string;
  user_id: number;
  organization_id: number;
  auth_token: string;
  email: string;
  sandbox_provisioned?: boolean;
  role?: string;
}

export interface EnableSandboxResponse {
  message: string;
  sandbox_provisioned: boolean;
  role: string;
  organization_id: number;
  auth_token: string;
  sandbox_id?: number | null;
  expires_at?: string | null;
  api_credentials?: {
    client_id?: string;
    api_key?: string;
    api_key_prefix?: string;
    message?: string;
    error?: string;
  } | null;
}

export interface OrganizationUser {
  id: number;
  name: string;
  email: string;
  role: string;
  phone_number?: string;
  last_login?: string;
  created_at: string;
  terms_accepted: boolean;
  hipaa_accepted: boolean;
  user_org_role: 'super_admin' | 'org_admin' | 'ops_admin' | 'program_manager' | 'supervisor' | 'care_coordinator' | 'promotora' | 'manager' | 'staff' | 'viewer' | 'member';
  // Display name(s) of the assigned org role(s) from role_terminology — e.g.
  // "Disaster Case Manager" for custom roles not in the hardcoded USER_ROLES map.
  role_names?: string | null;
  is_primary: boolean;
  user_type?: 'user' | 'member' | 'invited';  // 'user' for staff/admin, 'member' for member portal users, 'invited' for pending invitations (id is negative)
  // Present only for user_type='invited' rows — the real positive id of the
  // underlying user_invitations row. Used to route Edit through
  // PATCH /api/cbos/{cbo}/users/invitations/{invitation_id} since the
  // OrganizationUser.id is a synthetic negative value for these.
  invitation_id?: number;
  // Account status from users.status. Deactivated users are still listed so
  // admins can see/reactivate them — the UI highlights them.
  status?: 'active' | 'inactive';
  // Consent tracking - version and signing date
  terms_signed_version?: string;
  terms_signed_at?: string;
  hipaa_signed_version?: string;
  hipaa_signed_at?: string;
  // Triveny only: the real platform_shared.tenant_users.id (UUID). Lets
  // the FE call /api/platform/roles/users/{uuid} for role assignment
  // without having to round-trip the OrganizationUser.id pseudo-int.
  tenant_user_id?: string;
  // Portal members only (user_type='member'): `id` is members.id, but the
  // account-scoped endpoints (2FA, email change, name/phone PATCH) key on
  // users.id — an overlapping integer space, so the member id must never be
  // sent to them. Null when the member has no portal account yet.
  account_user_id?: number | null;
  // Triveny only: Privacy + AI consents (HIPAA is N/A on platform).
  // Read from platform_shared.tenant_consent_status by the bridge.
  privacy_accepted?: boolean | null;
  privacy_signed_version?: string | null;
  privacy_signed_at?: string | null;
  ai_accepted?: boolean | null;
  ai_signed_version?: string | null;
  ai_signed_at?: string | null;
}

export interface UserRoleAssignment {
  id: number;
  user_id: number;
  organization_id: number;
  role_terminology_id: number;
  role_type: string;
  display_name_en: string;
  display_name_es?: string;
  system_role: string;
  access_level?: number;
  abbreviation?: string;
  icon?: string;
  color?: string;
  is_field_worker?: boolean;
  is_primary: boolean;
  assigned_by?: number;
  assigned_by_name?: string;
  assigned_at?: string;
}

/** A single entry in the role-assignment audit log (TRUS-308). */
export interface RoleAuditEntry {
  id: number;
  user_id?: number;
  user_name?: string;
  action: 'assigned' | 'removed' | 'set_primary' | string;
  role_terminology_id?: number;
  role_type?: string;
  role_display_name?: string;
  is_primary?: boolean;
  actor_user_id?: number;
  actor_name?: string;
  created_at: string;
}

export const USER_ROLES = {
  // ----- Triveny platform roles (platform_shared.tenant_users.role) -----
  // The role badge in UserManagement looks up by role_key; without these
  // entries it falls back to USER_ROLES.viewer and renders every Triveny
  // user as "Viewer" — including the platform admin who created the
  // tenant. Colors mirror the equivalent healthcare role tier.
  platform_admin: {
    label: 'Platform Admin',
    description: 'Triveny-internal admin with full platform access across tenants',
    color: '#dc2626',  // same red as super_admin
  },
  tenant_admin: {
    label: 'Tenant Admin',
    description: 'Full access to this tenant — users, roles, billing, all surfaces',
    color: '#ea580c',  // same orange as org_admin
  },
  tenant_user: {
    label: 'Team Member',
    description: 'Standard tenant user — design / market / engage; no user or role management',
    color: '#16a34a',  // same green as staff
  },
  tenant_viewer: {
    label: 'Read-only Viewer',
    description: 'Read-only access to analytics, pipeline, and content',
    color: '#2563eb',  // same blue as viewer
  },

  // ----- Healthcare (WeaveIn) roles -----
  super_admin: {
    label: 'Super Administrator',
    description: 'Full system access across all organizations',
    color: '#dc2626'
  },
  org_admin: {
    label: 'Organization Administrator',
    description: 'Full access to organization settings, users, and data',
    color: '#ea580c'
  },
  ops_admin: {
    label: 'Operations Administrator',
    description: 'Internal platform operations access (cannot delete organizations or users)',
    color: '#d97706'
  },
  program_manager: {
    label: 'Program Manager',
    description: 'Manages program configuration, enrollment, and reporting',
    color: '#7c3aed'
  },
  supervisor: {
    label: 'Supervisor',
    description: 'Oversees staff and reviews care plans and documentation',
    color: '#9333ea'
  },
  care_coordinator: {
    label: 'Care Coordinator',
    description: 'Coordinates member care, manages referrals, and tracks outcomes',
    color: '#0ea5e9'
  },
  promotora: {
    label: 'Promotora',
    description: 'Community health worker providing outreach and member support',
    color: '#14b8a6'
  },
  manager: {
    label: 'Manager',
    description: 'Can manage programs, members, and workflows',
    color: '#ca8a04'
  },
  staff: {
    label: 'Staff',
    description: 'Can view and edit assigned members and enrollments',
    color: '#16a34a'
  },
  member: {
    label: 'Member',
    description: 'Access to member portal for care coordination services',
    color: '#0891b2'
  },
  viewer: {
    label: 'Viewer',
    description: 'Read-only access to organization data',
    color: '#2563eb'
  }
} as const;

// ============================================================================
// API CLIENT
// ============================================================================

class UserManagementAPI {
  private client: AxiosInstance;

  // Default to the product-aware main API URL — ms-api-hots on Triveny,
  // weavein-api on WeaveIn. Previously this read VITE_WEAVEIN_API_URL
  // directly, which on Triveny Vercel points at weavein-api (no
  // platform-JWT dual-auth) → every call 401'd with "Invalid token".
  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    // Use full backend URL to avoid proxy JWT validation issues
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('🔐 UserManagement API: No auth token found in localStorage');
      }
      console.log('🔐 UserManagement API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        hasToken: !!token,
      });
      return config;
    });

    // Add response error interceptor for detailed logging
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url;
        const method = error.config?.method?.toUpperCase();

        if (status === 401) {
          console.error('🔐 UserManagement API 401 Unauthorized:', {
            url,
            method,
            message: error.response?.data?.detail || error.message,
            hasToken: !!localStorage.getItem('auth_token'),
          });
        } else if (status === 405) {
          console.error('🔐 UserManagement API 405 Method Not Allowed:', {
            url,
            method,
            message: error.response?.data?.detail || error.message
          });
        } else {
          console.error('🔐 UserManagement API Error:', {
            status,
            url,
            method,
            message: error.response?.data?.detail || error.message
          });
        }
        return Promise.reject(error);
      }
    );
  }

  // ========================================================================
  // USER INVITATION ENDPOINTS
  // ========================================================================

  /**
   * Invite a new user to join an organization
   */
  async inviteUser(cboId: number, invitation: UserInvitationCreate): Promise<UserInvitation> {
    const response = await this.client.post<UserInvitation>(
      `/api/cbos/${cboId}/users/invite`,
      invitation
    );
    return response.data;
  }

  /**
   * Invite the staff accounts in an org that exist but were never claimed.
   *
   * The bulk staff loaders create users + memberships with no password and send
   * nothing, so a loaded org has a full roster nobody can log into and no
   * pending invitations to show. This is the step that notifies them.
   *
   * ALWAYS dry-run first and show the result: `dry_run` defaults to true
   * server-side precisely because the real call mails an entire staff roster
   * and cannot be recalled. The dry run is also the authority on who is
   * actually invitable — the user list has no password column, so the UI can
   * only guess from last_login.
   */
  async bulkInviteUsers(cboId: number, payload: BulkInviteRequest): Promise<BulkInviteResult> {
    const response = await this.client.post<BulkInviteResult>(
      `/api/cbos/${cboId}/users/bulk-invite`,
      payload
    );
    return response.data;
  }

  /**
   * List all invitations for an organization
   */
  async listInvitations(cboId: number, status?: string): Promise<UserInvitation[]> {
    const params = status ? { status } : {};
    const response = await this.client.get<UserInvitation[]>(
      `/api/cbos/${cboId}/users/invitations`,
      { params }
    );
    return response.data;
  }

  /**
   * Cancel a pending invitation
   * @param invitationType - 'user' for staff/admin invitations, 'member' for member portal invitations
   */
  async cancelInvitation(cboId: number, invitationId: number, invitationType: 'user' | 'member' = 'user'): Promise<void> {
    await this.client.delete(`/api/cbos/${cboId}/users/invitations/${invitationId}`, {
      params: { invitation_type: invitationType }
    });
  }

  async resendInvitation(cboId: number, invitationId: number, invitationType: 'user' | 'member' = 'user'): Promise<{ message: string }> {
    const response = await this.client.post(`/api/cbos/${cboId}/users/invitations/${invitationId}/resend`, null, {
      params: { invitation_type: invitationType }
    });
    return response.data;
  }

  /**
   * Update a pending user_invitation. Backed by
   * PATCH /api/cbos/{cbo_id}/users/invitations/{invitation_id}.
   *
   * Supports two edit categories:
   *   - role / additional_roles (the BUG-005 use case)
   *   - name / phone_number / email (typo fix before acceptance — Somos
   *     2026-06-19 incident)
   *
   * Only invitations in 'pending' or 'held' status are editable. Mirrors
   * the per-role permission gates of POST /invite — caller sees 403 if
   * their role can't assign the requested target role.
   *
   * Note: name + phone_number are silently ignored on the Triveny platform
   * branch (platform_shared.tenant_invitations doesn't carry those
   * columns). Email IS supported on both branches.
   */
  async updateInvitation(
    cboId: number,
    invitationId: number,
    body: {
      role?: string;
      additional_roles?: string[];
      name?: string;
      phone_number?: string;
      email?: string;
      require_2fa?: boolean;
    }
  ): Promise<{ ok: boolean; invitation_id: number; updated_fields: string[] }> {
    const response = await this.client.patch(
      `/api/cbos/${cboId}/users/invitations/${invitationId}`,
      body
    );
    return response.data;
  }

  /**
   * Release a held user invitation — sends the email and flips status to 'pending'.
   * Held invitations are created when the wizard's send_now flag is false.
   */
  async sendHeldInvitation(cboId: number, invitationId: number): Promise<{ message: string }> {
    const response = await this.client.post(`/api/cbos/${cboId}/users/invitations/${invitationId}/send`);
    return response.data;
  }

  // ========================================================================
  // USER REGISTRATION
  // ========================================================================

  /**
   * Register a new user account using an invitation token
   */
  async registerWithInvite(data: UserRegistrationWithInvite): Promise<{ message: string; user_id: number; email: string }> {
    const response = await this.client.post('/api/auth/register-with-invite', data);
    return response.data;
  }

  /**
   * Verify an invitation token (without registering)
   */
  async verifyInvitationToken(token: string): Promise<{ valid: boolean; invitation?: Partial<UserInvitation> }> {
    try {
      // The backend doesn't have a specific verify endpoint, so we'll handle this in the component
      // by attempting to register and catching errors
      return { valid: true };
    } catch (error) {
      return { valid: false };
    }
  }

  /**
   * Self-service signup - Create new organization and user account
   * User is automatically assigned as 'viewer' role
   */
  async selfServiceSignup(data: SelfServiceSignup): Promise<SelfServiceSignupResponse> {
    const response = await this.client.post<SelfServiceSignupResponse>(
      '/api/auth/self-signup',
      data
    );
    return response.data;
  }

  /**
   * Self-serve: upgrade the caller's own self-signup org to a 30-day sandbox
   * (full admin access, seeded demo data, sandbox API credentials). Post-signup
   * counterpart to the sandbox toggle on the signup form — for viewers who
   * already signed up and want to test without booking a demo first.
   */
  async enableSandbox(): Promise<EnableSandboxResponse> {
    const response = await this.client.post<EnableSandboxResponse>(
      '/api/auth/self-signup/enable-sandbox',
      {}
    );
    return response.data;
  }

  // ========================================================================
  // PASSWORD MANAGEMENT
  // ========================================================================

  /**
   * Request a password reset email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  /**
   * Reset password using a reset token
   */
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/auth/reset-password', {
      reset_token: token,
      new_password: newPassword
    });
    return response.data;
  }

  /**
   * Change password for logged-in user
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  }

  // ========================================================================
  // USER MANAGEMENT
  // ========================================================================

  /**
   * List all users in an organization
   */
  /**
   * List an organization's users.
   *
   * By default this returns BOTH staff (user_type='user') and portal members
   * (user_type='member') — the admin Users screen needs both. Callers that
   * only want staff (e.g. the messaging "Add people" / "New DM" staff picker)
   * MUST pass { staffOnly: true }, otherwise portal members leak into the
   * staff list (TRUS-434). exclude_internal drops platform admins + internal
   * automation accounts.
   */
  async listUsers(
    cboId: number,
    opts: { staffOnly?: boolean; excludeInternal?: boolean } = {}
  ): Promise<OrganizationUser[]> {
    const params: Record<string, boolean> = {};
    if (opts.staffOnly) params.staff_only = true;
    if (opts.excludeInternal) params.exclude_internal = true;
    const response = await this.client.get<OrganizationUser[]>(
      `/api/cbos/${cboId}/users`,
      { params }
    );
    return response.data;
  }

  /**
   * Remove a user or member from an organization
   * @param userType - 'user' for staff/admin, 'member' for member portal users
   * @param confirmAccountWide - required to proceed when the target holds an
   *   account-level role (super_admin/ops_admin): the backend returns 409 with
   *   `detail.affected_organizations` until this is passed, because the removal
   *   revokes access to EVERY org, not just this one.
   */
  async removeUser(
    cboId: number,
    userId: number,
    userType: 'user' | 'member' = 'user',
    confirmAccountWide = false
  ): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/cbos/${cboId}/users/${userId}`, {
      params: {
        user_type: userType,
        ...(confirmAccountWide ? { confirm_account_wide: true } : {})
      }
    });
    return response.data;
  }

  // ========================================================================
  // USER PROGRAM ASSIGNMENT ENDPOINTS (Enhanced)
  // ========================================================================

  /**
   * Get all program assignments for a user with enhanced role details
   */
  async getUserProgramAssignments(userId: number, cboId: number): Promise<UserProgramAssignment[]> {
    const response = await this.client.get<UserProgramAssignment[]>(
      `/api/users/${userId}/program-assignments`,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
    return response.data;
  }

  /**
   * Assign a program to a user with enhanced role and permission support
   */
  async assignProgramToUser(
    userId: number,
    cboId: number,
    assignment: UserProgramAssignmentCreate
  ): Promise<{ message: string; assignment: UserProgramAssignment }> {
    const response = await this.client.post(
      `/api/users/${userId}/program-assignments`,
      assignment,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
    return response.data;
  }

  /**
   * Remove a program assignment from a user
   */
  async removeProgramAssignment(userId: number, assignmentId: number, cboId: number): Promise<void> {
    await this.client.delete(
      `/api/users/${userId}/program-assignments/${assignmentId}`,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
  }

  /**
   * Change user's role within a specific program assignment
   */
  async changeProgramRole(
    userId: number,
    assignmentId: number,
    cboId: number,
    roleChange: ProgramRoleChange
  ): Promise<{ message: string; assignment: UserProgramAssignment }> {
    const response = await this.client.put(
      `/api/users/${userId}/program-assignments/${assignmentId}/role`,
      roleChange,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
    return response.data;
  }

  /**
   * Get organization roles that can be assigned to users for programs
   * Excludes admin roles (org_admin, super_admin)
   */
  async getAssignableRoles(cboId: number, forProgramId?: number): Promise<AssignableRole[]> {
    const params: Record<string, string> = {};
    if (forProgramId) {
      params.for_program_id = forProgramId.toString();
    }
    const response = await this.client.get<AssignableRole[]>(
      `/api/organizations/${cboId}/assignable-roles`,
      { headers: { 'X-CBO-ID': cboId.toString() }, params }
    );
    return response.data;
  }

  /**
   * Get user's effective permissions (base role + program assignments + overrides)
   */
  async getUserEffectivePermissions(
    userId: number,
    cboId: number,
    programId?: number
  ): Promise<UserEffectivePermissions> {
    const params: Record<string, string> = {};
    if (programId) {
      params.program_id = programId.toString();
    }
    const response = await this.client.get<UserEffectivePermissions>(
      `/api/users/${userId}/effective-permissions`,
      { headers: { 'X-CBO-ID': cboId.toString() }, params }
    );
    return response.data;
  }

  /**
   * Get all program leads for a program
   */
  async getProgramLeads(programId: number, cboId: number): Promise<ProgramLead[]> {
    const response = await this.client.get<ProgramLead[]>(
      `/api/programs/${programId}/leads`,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
    return response.data;
  }

  /**
   * Check if user is a program lead for the specified program
   */
  async isUserProgramLead(
    userId: number,
    programId: number,
    cboId: number
  ): Promise<{ user_id: number; program_id: number; is_program_lead: boolean }> {
    const response = await this.client.get(
      `/api/users/${userId}/is-program-lead/${programId}`,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
    return response.data;
  }

  // ========================================================================
  // USER MEMBER ASSIGNMENT ENDPOINTS
  // ========================================================================

  /**
   * Get all member assignments for a user
   */
  async getUserMemberAssignments(userId: number, cboId: number): Promise<MemberAssignment[]> {
    const response = await this.client.get<MemberAssignment[]>(
      `/api/users/${userId}/member-assignments`,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
    return response.data;
  }

  /**
   * Assign a member to a user
   */
  async assignMemberToUser(
    userId: number,
    cboId: number,
    assignment: MemberAssignmentCreate
  ): Promise<{ message: string; assignment: MemberAssignment }> {
    const response = await this.client.post(
      `/api/users/${userId}/member-assignments`,
      assignment,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
    return response.data;
  }

  /**
   * Remove a member assignment from a user
   */
  async removeMemberAssignment(userId: number, assignmentId: number, cboId: number): Promise<void> {
    await this.client.delete(
      `/api/users/${userId}/member-assignments/${assignmentId}`,
      { headers: { 'X-CBO-ID': cboId.toString() } }
    );
  }

  // ========================================================================
  // USER ORGANIZATION ROLE ENDPOINTS (Multi-Role Management)
  // ========================================================================

  async getUserRoles(cboId: number, userId: number): Promise<UserRoleAssignment[]> {
    const response = await this.client.get<UserRoleAssignment[]>(
      `/api/cbos/${cboId}/users/${userId}/roles`
    );
    return response.data;
  }

  async addUserRole(cboId: number, userId: number, data: { role_type: string; is_primary?: boolean }): Promise<UserRoleAssignment> {
    const response = await this.client.post<UserRoleAssignment>(
      `/api/cbos/${cboId}/users/${userId}/roles`,
      data
    );
    return response.data;
  }

  async removeUserRole(cboId: number, userId: number, roleId: number): Promise<void> {
    await this.client.delete(`/api/cbos/${cboId}/users/${userId}/roles/${roleId}`);
  }

  async setUserPrimaryRole(cboId: number, userId: number, roleId: number): Promise<void> {
    await this.client.put(`/api/cbos/${cboId}/users/${userId}/roles/${roleId}/primary`);
  }

  async getAllOrgRoles(cboId: number): Promise<{ id: number; role_type: string; display_name_en: string; display_name_es?: string; system_role: string; abbreviation?: string; icon?: string; color?: string }[]> {
    const response = await this.client.get(`/api/cbos/${cboId}/all-roles`);
    return response.data;
  }

  /** Assignment history for one user in an org (who assigned/removed/set-primary which role, when). TRUS-308. */
  async getUserRoleAudit(cboId: number, userId: number, limit = 200): Promise<RoleAuditEntry[]> {
    const response = await this.client.get<RoleAuditEntry[]>(
      `/api/cbos/${cboId}/users/${userId}/role-audit`,
      { params: { limit } }
    );
    return response.data;
  }

  /** Org-wide role-assignment audit feed (admins only). TRUS-308. */
  async getOrgRoleAudit(cboId: number, limit = 100): Promise<RoleAuditEntry[]> {
    const response = await this.client.get<RoleAuditEntry[]>(
      `/api/cbos/${cboId}/role-audit`,
      { params: { limit } }
    );
    return response.data;
  }

  /**
   * Preview helper (TRUS-308): whether a role would be allowed to EDIT member
   * profiles. Lets a super_admin previewing a custom role (e.g. ECM Care Manager)
   * test that role's member-edit permission. Admins only.
   */
  async getPreviewMemberEditCapability(
    cboId: number,
    opts: { orgRoleId?: number; roleType?: string }
  ): Promise<{
    org_role_id: number;
    role_type: string;
    system_role: string;
    display_name_en: string;
    can_edit_members: boolean;
    access_level: 'full' | 'view_only';
  }> {
    const params: Record<string, string | number> = {};
    if (opts.orgRoleId != null) params.org_role_id = opts.orgRoleId;
    if (opts.roleType) params.role_type = opts.roleType;
    const response = await this.client.get(
      `/api/cbos/${cboId}/preview/member-edit-capability`,
      { params }
    );
    return response.data;
  }

  // ========================================================================
  // INVITATION VERIFICATION & MULTI-ORG ACCEPTANCE
  // ========================================================================

  async verifyInvitation(token: string): Promise<{
    email: string;
    name: string;
    organization_name: string;
    role: string;
    cbo_id?: number;
    is_existing_user: boolean;
    /** Pre-staged personal mobile (if the inviting admin filled one in). */
    phone_number?: string | null;
  }> {
    const response = await this.client.get(`/api/auth/verify-invitation/${token}`);
    return response.data;
  }

  async acceptOrgInvitation(token: string): Promise<{ message: string; user_id: number }> {
    const response = await this.client.post('/api/auth/accept-org-invitation', { invitation_token: token });
    return response.data;
  }

  // ========================================================================
  // ADMIN USER MANAGEMENT ACTIONS
  // ========================================================================

  /**
   * Admin-initiated password reset for a user
   */
  async adminResetPassword(cboId: number, userId: number): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(
      `/api/cbos/${cboId}/users/${userId}/reset-password`
    );
    return response.data;
  }

  /**
   * Activate or deactivate a user
   */
  async updateUserStatus(cboId: number, userId: number, status: 'active' | 'inactive'): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(
      `/api/cbos/${cboId}/users/${userId}/status`,
      { status }
    );
    return response.data;
  }

  /**
   * Edit user details (name, phone_number)
   */
  async editUserDetails(cboId: number, userId: number, data: { name?: string; phone_number?: string }): Promise<{ message: string }> {
    const response = await this.client.patch<{ message: string }>(
      `/api/cbos/${cboId}/users/${userId}`,
      data
    );
    return response.data;
  }

  // ========================================================================
  // BULK REASSIGNMENT
  // ========================================================================

  async bulkReassignCases(request: {
    from_user_id: number;
    to_user_id: number;
    reason?: string;
    include_dm_channels?: boolean;
    include_member_assignments?: boolean;
    include_enrollments?: boolean;
  }): Promise<{
    success: boolean;
    from_user_id: number;
    to_user_id: number;
    member_assignments_reassigned: number;
    dm_channels_reassigned: number;
    enrollments_reassigned: number;
    errors: Array<{ member_id?: number; channel_id?: number; error: string }>;
  }> {
    const response = await this.client.post('/api/users/bulk-reassign', request);
    return response.data;
  }
}

// Export singleton instance
export const userManagementAPI = new UserManagementAPI();

// Export class for custom instances
export default UserManagementAPI;
